import type { NotificationWorkflowItem } from '../../../shared/services/notification/workflow';
import { parseNumeric, postRequest } from '../../../shared/utils';

type Decision = 'ACCEPT' | 'REJECT';

type ResolveResult = {
  handled: boolean;
};

function parseText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isNpiSteeringHtzPayload(payload: Record<string, unknown>): boolean {
  const screen = parseText(payload.screen);
  return screen ? screen.toUpperCase() === 'NPI_STEERING' : true;
}

/**
 * ACCEPT flow: permanently deletes the target `sheet1` row.
 */
function buildSheet1DeleteRowPayload(rowId: number) {
  return {
    pkey: 'id1',
    schema: 'npi',
    table: 'sheet1',
    deleteIds: [rowId],
    insertObjects: [],
    updateObjects: [],
  };
}

/**
 * REJECT flow: rolls back `sheet1.indus_id` to its previous value
 * and stamps updater metadata.
 */
function buildSheet1RollbackPreviousIndusPayload(
  rowId: number,
  updatedBy: number,
  previousIndusId: number,
) {
  return {
    pkey: 'id1',
    schema: 'npi',
    table: 'sheet1',
    deleteIds: [],
    insertObjects: [],
    updateObjects: [
      {
        where: {
          id1: {
            _eq: rowId,
          },
        },
        _set: {
          indus_id: previousIndusId,
          updated_by: updatedBy,
          update_date: new Date().toISOString().substring(0, 19),
        },
      },
    ],
  };
}

export async function resolveNpiSteeringDeletionDecision({
  item,
  decision,
}: {
  item: NotificationWorkflowItem;
  decision: Decision;
}): Promise<ResolveResult> {
  const payload = (item.payload ?? {}) as Record<string, unknown>;

  if (!isNpiSteeringHtzPayload(payload)) {
    return { handled: false };
  }

  const rowId = parseNumeric(payload.rowId);
  const screenGridId = parseNumeric(payload.screenGridId);
  const previousIndusId = parseNumeric(payload.previousIndusId);
  const decisionBy = parseNumeric(item.userId);

  if (rowId == null || screenGridId == null) {
    return { handled: false };
  }

  let tables: Array<ReturnType<typeof buildSheet1DeleteRowPayload>> = [];

  if (decision === 'ACCEPT') {
    tables = [buildSheet1DeleteRowPayload(rowId)];
  }

  if (decision === 'REJECT') {
    if (decisionBy == null || previousIndusId == null) {
      return { handled: false };
    }

    tables = [buildSheet1RollbackPreviousIndusPayload(rowId, decisionBy, previousIndusId)];
  }

  if (!tables.length) {
    return { handled: false };
  }

  const result = await postRequest('/api/tables', {
    screenGridId,
    tables,
  });

  if (result?.errors?.length) {
    throw new Error(
      result.errors[0]?.message ?? 'Failed to execute NPI Steering deletion decision',
    );
  }

  return { handled: true };
}
